import React, { useCallback } from "react"
import { InlineButtons } from "@tenforce/product-fields"
import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { executeAction, handleExecutionStarted } from "../../../scripting/slice"
import { IInlineButtonsField } from "../../index"

export interface Props {
    field: IInlineButtonsField
}

export const InlineButtonsField = ({ field }: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const { isLoading, currentFieldInEditMode = "" } = useAppSelector(
        (state: GlobalState) => state.fields,
    )
    const { isScriptSaved, isScriptSavingFailed, isScriptRunning } =
        useAppSelector((state: GlobalState) => state.scripting)
    const {
        position: _position,
        odataType: _odataType,
        value,
        itemId,
        description,
        ...fieldProps
    } = field

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    const localActions = value
        .filter((action) => {
            return !!action.id
        })
        .map((action) => {
            const actionId = action.id ?? ""
            return {
                ...action,
                onClick: () => handleClick(actionId),
                isLoading: isScriptRunning[actionId] ?? false,
                isRecentlyExecuted: isScriptSaved[actionId] ?? false,
                isRecentlyFailed: isScriptSavingFailed[actionId] ?? false,
            }
        })

    const handleClick = useCallback(
        async (id: string): Promise<void> => {
            dispatch(handleExecutionStarted(id))
            await dispatch(
                executeAction({
                    action: id,
                    itemId: field.itemId,
                    fieldId: field.fieldId,
                }),
            )
        },
        [localActions],
    )

    return (
        <InlineButtons
            {...fieldProps}
            isDisabled={isDisabled}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            actions={localActions}
            isLoading={isLoading ?? false}
        />
    )
}

export default InlineButtonsField
